import { Component, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from './core/services/auth.service';
import { RequestsService } from './core/services/utils/requests.service';
import { NotificationService } from './core/services/notification.service';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from '@angular/platform-browser';
import { SharedService } from './core/services/shared.service';
import { ToastService } from './core/services/utils/toast.service';
import { MatDialog } from '@angular/material/dialog';
import { Platform } from '@ionic/angular';
import { LoggerService } from './core/services/utils/logger.service';
import { NetworkStatusService } from './core/services/network-status.service';
import { distinctUntilChanged, map, skip, tap } from 'rxjs';
import { isAccessTokenValid } from './shared/utils/check.utils';
import { UsageTrackingService } from './core/services/usage-tracking.service';
import { ClientLogsService } from './core/services/client-logs.service';
import { MonitoringSessionService } from './core/services/monitoring-session.service';
import { MonitoringPreferencesService } from './core/services/monitoring-preferences.service';

@Component({
  selector: 'app-root',
  imports: [
    CommonModule,
    RouterOutlet
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppComponent),
      multi: true,
    }
  ]
})
export class AppComponent {
  public isMobile: boolean = false;

  constructor(
    private translateService: TranslateService,
    private requestsService: RequestsService,
    private notificationService: NotificationService,
    private authService: AuthService,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private sharedService: SharedService,
    public dialog: MatDialog,
    private toastService: ToastService,
    private platform: Platform,
    private loggerService: LoggerService,
    private networkStatusService: NetworkStatusService,
    private usageTrackingService: UsageTrackingService,
    private clientLogs: ClientLogsService,
    private monitoringSession: MonitoringSessionService,
    private monitoringPreferences: MonitoringPreferencesService,
  ) {
    this.platform.ready().then(() => {
      this.initBusinessLogic();
    })
  }

  private initBusinessLogic() {
    this.monitoringSession.initialize();
    this.usageTrackingService.initialize();
    this.clientLogs.initialize();
    this.loadCustomMatIcons();
    this.notificationService.listenForNotifications();
    this.notificationService.checkNotificationPermission();
    this.notificationService.checkLocalNotifications();
    this.initializeLanguage(navigator.language);
    this.requestsService.currentUser$.pipe(
      map((user) => `${user?.id}:${user?.role}:${user?.building?.id}`),
      distinctUntilChanged(),
      skip(1)
    ).subscribe(() => {
      this.sharedService.stopWss();
      this.sharedService.clearAlerts();
      this.notificationService.checkNotificationPermission();
      if (isAccessTokenValid()) {
        this.sharedService.startWss(this.toastService);
      } else if (this.hasStoredAccessToken()) {
        this.refreshAccessTokenAndRestartWss();
      }
    });
    if (isAccessTokenValid()) {
      this.sharedService.startWss(this.toastService);
    } else if (this.hasStoredAccessToken()) {
      this.refreshAccessTokenAndRestartWss();
    }
    this.networkStatusService.initNetworkListener();
    this.networkStatusService.isOnline$.pipe(
      skip(1)
    ).subscribe((isOnline) => {
      if (isOnline) {
        this.monitoringPreferences.refresh();
        this.usageTrackingService.onNetworkAvailable();
        this.notificationService.checkNotificationPermission();
        if (isAccessTokenValid()) {
          this.sharedService.startWss(this.toastService);
        } else if (this.hasStoredAccessToken()) {
          this.refreshAccessTokenAndRestartWss();
        }
      } else {
        this.sharedService.stopWss();
      }
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.notificationService.checkNotificationPermission();
        if (isAccessTokenValid()) {
          this.sharedService.startWss(this.toastService);
        } else if (this.hasStoredAccessToken()) {
          this.refreshAccessTokenAndRestartWss();
        }
      } else if (document.visibilityState === 'hidden') {
        this.sharedService.stopWss();
      }
    });
  }

  private hasStoredAccessToken(): boolean {
    return !!localStorage.getItem('ACCESS_TOKEN');
  }

  private refreshAccessTokenAndRestartWss() {
    this.authService.callRefreshToken$().pipe(
      tap(() => {
        this.sharedService.startWss(this.toastService);
        this.usageTrackingService.onAuthenticationAvailable();
      })
    ).subscribe({
      error: () => undefined
    });
  }

  loadCustomMatIcons() {
    this.matIconRegistry.addSvgIcon(
      `fall`,
      this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/files/customSvg/fall.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "room_in",
      this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/files/customSvg/room_in.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "room_out",
      this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/files/customSvg/room_out.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "bed_in",
      this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/files/customSvg/bed_in.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "bed_out",
      this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/files/customSvg/bed_out.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "bathroom_in",
      this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/files/customSvg/bathroom_in.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "device_offline",
      this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/files/customSvg/device_offline.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "device_online",
      this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/files/customSvg/device_online.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "mentorage",
      this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/files/customSvg/mentorage.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "bed",
      this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/files/customSvg/bed.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "bed_alternative",
      this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/files/customSvg/bed_alternative.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "bed_disabled",
      this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/files/customSvg/bed_disabled.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "room_disabled",
      this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/files/customSvg/room_disabled.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "fall_disabled",
      this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/files/customSvg/fall_disabled.svg")
    );

    this.matIconRegistry.addSvgIcon(
      "bed_exit_delay_clock",
      this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/files/customSvg/bed_exit_delay_clock.svg")
    );
  }

  /**
   * @description Imposto le traduzioni sulla base della lingua dello user o, se non esistente, del browser, di default metto 'it'
   * @param browserLanguage 
   */
  initializeLanguage(browserLanguage: string) {
    if (['it', 'en', 'fr'].includes(this.requestsService.getCurrentUser()?.language || "")) {
      this.translateService.use(this.requestsService.getCurrentUser()!.language);
    } else {
      if (browserLanguage.includes('en-')) {
        this.translateService.use('en');
      } else if (browserLanguage.includes('fr-')) {
        this.translateService.use('fr');
      } else {
        this.translateService.use('it');
      }
    }
  }

}
